import { useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/auth-store';
import { httpsCallable } from '@/lib/firebase/functions';
import { functions } from '@/config/firebase';
import { idbCache } from '@/lib/cache/idb-cache';

export interface AdminDetails {
  name?: string;
  email?: string;
  photoURL?: string;
  /** True while the CF lookup is in flight and we have nothing cached yet. */
  loading?: boolean;
}

interface CFResponse {
  status?: string;
  userResponse?: { uid?: string; name?: string; email?: string; photoURL?: string };
}

const CACHE_NAMESPACE = 'org_data';
const CACHE_KEY = (uid: string) => `admin_details_${uid}`;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 24h

const memoryCache = new Map<string, AdminDetails>();
const pending = new Map<string, Promise<AdminDetails | null>>();

async function resolveAdminDetails(uid: string, email: string | null): Promise<AdminDetails | null> {
  const existing = pending.get(uid);
  if (existing) return existing;

  const promise = (async (): Promise<AdminDetails | null> => {
    try {
      const call = httpsCallable<Record<string, unknown>, CFResponse>(
        functions,
        'new_callServiceAuthorizedAPI',
      );
      const resp = await call({
        appCode: 'sharedadmin',
        action: 'GET_USERDETAILS_FOR_FBUID',
        uaFBUserID: uid,
        uaFBEmail: email || null,
      });
      if (resp.data?.status === 'success' && resp.data.userResponse) {
        const details: AdminDetails = {
          name: resp.data.userResponse.name,
          email: resp.data.userResponse.email,
          photoURL: resp.data.userResponse.photoURL,
        };
        memoryCache.set(uid, details);
        // Persist for next page load — 24h TTL matches the existing user
        // detail cache used elsewhere in the codebase.
        idbCache.set(CACHE_NAMESPACE, CACHE_KEY(uid), details, CACHE_TTL_MS).catch(() => {});
        return details;
      }
      return null;
    } catch (err) {
      console.warn('[useAdminDetails] CF lookup failed:', err);
      return null;
    } finally {
      pending.delete(uid);
    }
  })();
  pending.set(uid, promise);
  return promise;
}

/**
 * Resolve the current shared-admin's display name / email / photo.
 *
 * Lookup order:
 *  1. `invitedAdminList[uid]` (in-session, populated when the user picks the admin)
 *  2. In-memory module cache (this page visit)
 *  3. IDB cache (persists 24h across reloads)
 *  4. Cloud Function `GET_USERDETAILS_FOR_FBUID` — only fires if nothing above hit.
 *
 * Returns `null` if there is no shared admin. Otherwise always returns an
 * object; `loading: true` during the initial fetch so callers can show the
 * UID as a placeholder until the name resolves.
 */
export function useAdminDetails(): AdminDetails | null {
  const selectedAdminUserId = useAuthStore((s) => s.selectedAdminUserId);
  const selectedAdminUserEmail = useAuthStore((s) => s.selectedAdminUserEmail);
  const invitedAdminList = useAuthStore((s) => s.invitedAdminList);

  const invitedEntry = selectedAdminUserId ? invitedAdminList?.[selectedAdminUserId] : null;
  const seedName = invitedEntry?.invitedAdminUserName;
  const seedEmail = invitedEntry?.invitedAdminUserEmail || selectedAdminUserEmail || '';
  const seedPic = invitedEntry?.invitedAdminUserPic;

  // Synchronous hit from module-level memory cache — avoids a loading flash
  // if the admin was resolved earlier in this tab.
  const memHit = selectedAdminUserId ? memoryCache.get(selectedAdminUserId) : undefined;
  const [fetched, setFetched] = useState<AdminDetails | null>(memHit ?? null);
  const [loading, setLoading] = useState<boolean>(!seedName && !memHit && !!selectedAdminUserId);

  useEffect(() => {
    if (!selectedAdminUserId) {
      setFetched(null);
      setLoading(false);
      return;
    }
    if (seedName) {
      setLoading(false);
      return;
    }
    if (memoryCache.has(selectedAdminUserId)) {
      setFetched(memoryCache.get(selectedAdminUserId) ?? null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    (async () => {
      // Try IDB first — much faster than a CF call and survives reloads.
      const idbHit = await idbCache.get<AdminDetails>(CACHE_NAMESPACE, CACHE_KEY(selectedAdminUserId)).catch(() => null);
      if (cancelled) return;
      if (idbHit) {
        memoryCache.set(selectedAdminUserId, idbHit);
        setFetched(idbHit);
        setLoading(false);
        return;
      }
      const details = await resolveAdminDetails(selectedAdminUserId, seedEmail || null);
      if (cancelled) return;
      if (details) setFetched(details);
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [selectedAdminUserId, seedName, seedEmail]);

  if (!selectedAdminUserId) return null;

  return {
    name: seedName || fetched?.name || '',
    email: seedEmail || fetched?.email || '',
    photoURL: seedPic || fetched?.photoURL,
    loading,
  };
}
